import React, { useState } from 'react';
import { Mail, MessageCircle, MessageSquare, Facebook, AlertTriangle, RefreshCw, Loader2, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { syncEmails } from '../../services/api';
import { formatListTimestamp, getTimestampForSort } from '../../utils/formatTimestamp';

const channelIcons = {
  email: Mail,
  whatsapp: MessageCircle,
  facebook: Facebook,
  chat: MessageSquare
};

const priorityColors = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500'
};

const statusColors = {
  open: 'text-green-600 bg-green-50',
  pending: 'text-yellow-600 bg-yellow-50',
  resolved: 'text-gray-500 bg-gray-100'
};

// Use the centralized timestamp formatting utility
const formatConversationTime = (dateString) => formatListTimestamp(dateString);

const ConversationList = ({ 
  conversations, 
  selectedConversation, 
  setSelectedConversation,
  agents,
  loading,
  onSync,
  pagination = { total: 0, limit: 50, skip: 0, hasMore: false },
  onLoadMore,
  loadingMore = false
}) => {
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await syncEmails();
      if (onSync) onSync();
      if (result.synced > 0) {
        // Will reload via onSync
      }
    } catch (error) {
      // Silently handle - sync might not be configured
    } finally {
      setSyncing(false);
    }
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? agent.name : 'Unassigned';
  };

  // Filter conversations based on search term
  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase();
    const customerName = conv.customer?.name?.toLowerCase() || '';
    const customerEmail = conv.customer?.email?.toLowerCase() || '';
    const customerPhone = conv.customer?.phone?.toLowerCase() || '';
    const subject = conv.subject?.toLowerCase() || '';
    const lastMessage = conv.last_message?.toLowerCase() || '';
    const assignedAgent = getAgentName(conv.assigned_to)?.toLowerCase() || '';
    
    return (
      customerName.includes(search) ||
      customerEmail.includes(search) ||
      customerPhone.includes(search) ||
      subject.includes(search) ||
      lastMessage.includes(search) ||
      assignedAgent.includes(search)
    );
  });

  return (
    <div className="w-96 bg-white border-r border-gray-200 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleSync}
              disabled={syncing}
              title="Sync emails"
              data-testid="sync-emails-btn"
            >
              <RefreshCw className={cn("w-4 h-4", syncing && "animate-spin")} />
            </Button>
            <span className="px-2.5 py-1 bg-[#eeb224]/10 text-[#eeb224] rounded-full text-sm font-medium">
              {pagination.total > 0 ? `${conversations.length} of ${pagination.total}` : `${conversations.length} active`}
            </span>
          </div>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#393d8a]/20 focus:border-[#393d8a] transition-all"
            data-testid="search-conversations-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="clear-search-btn"
            >
              ×
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="text-xs text-gray-500 mt-2">
            Found {filteredConversations.length} result{filteredConversations.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : conversations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No conversations</div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No conversations match your search</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredConversations.map((conv) => {
              const ChannelIcon = channelIcons[conv.channel] || Mail;
              const isEscalated = conv.escalated && conv.status !== 'resolved';
              return (
                <button
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={cn(
                    "w-full max-w-full p-4 text-left transition-all duration-200 hover:bg-gray-50 overflow-hidden",
                    selectedConversation?.id === conv.id && "bg-[#393d8a]/5 border-l-4 border-[#393d8a]",
                    isEscalated && selectedConversation?.id !== conv.id && "bg-red-50 border-l-4 border-red-500"
                  )}
                >
                  <div className="flex items-start gap-3 max-w-full">
                    {/* Avatar */}
                    <div className="relative">
                      <div className={cn(
                        "w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold text-sm",
                        isEscalated ? "bg-red-500" 
                          : conv.status === 'resolved' ? "bg-gray-300"
                          : conv.unread ? "bg-[#393d8a]" 
                          : "bg-gray-400"
                      )}>
                        {conv.customer?.avatar || conv.customer?.name?.charAt(0) || '?'}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm">
                        <ChannelIcon className="w-3 h-3 text-gray-600" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={cn(
                          "truncate flex-1 min-w-0",
                          conv.status === 'resolved' 
                            ? "font-normal text-gray-500" 
                            : conv.unread 
                              ? "font-semibold text-gray-900" 
                              : "font-medium text-gray-900"
                        )}>
                          {conv.customer?.name || 'Unknown'}
                        </span>
                        {isEscalated && (
                          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                        <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                          {formatConversationTime(conv.updated_at || conv.created_at)}
                        </span>
                      </div>
                      
                      <p className={cn(
                        "text-sm truncate mb-2",
                        conv.status === 'resolved'
                          ? "text-gray-400 font-normal"
                          : conv.unread 
                            ? "text-gray-900 font-medium" 
                            : "text-gray-600"
                      )}>
                        {conv.last_message || conv.lastMessage}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-xs font-medium capitalize",
                            isEscalated ? "bg-red-100 text-red-700" : statusColors[conv.status]
                          )}>
                            {isEscalated ? 'Escalated' : conv.status}
                          </span>
                          <div className="flex items-center gap-1">
                            <div className={cn("w-2 h-2 rounded-full", priorityColors[conv.priority])} />
                            <span className="text-xs text-gray-500 capitalize">{conv.priority}</span>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 truncate max-w-[100px]">
                          {getAgentName(conv.assigned_to || conv.assignedTo)}
                        </span>
                      </div>
                    </div>

                    {/* Unread indicator */}
                    {conv.unread && (
                      <div className="w-2.5 h-2.5 bg-[#eeb224] rounded-full mt-2 flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
            
            {/* Load More Button */}
            {pagination.hasMore && !searchTerm && (
              <div className="p-4 border-t border-gray-100">
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  data-testid="load-more-conversations-btn"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Load More ({pagination.total - conversations.length} remaining)
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
